import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import "./People.css";
import { artists } from "../../../data/artists";

// Quantas pessoas o carrossel avança a cada clique nas setas.
// Em telas estreitas, onde cabem menos que isso, avança só o que cabe (ver `scroll`).
const PEOPLE_PER_CLICK = 3;

// Voadores do carrossel = artistas de artists.ts.
// Entradas com `aliasOf` são só variantes de grafia de outro artista e não entram.
const people = Object.values(artists).filter((artist) => !artist.aliasOf);

interface PeopleProps {
  onPersonClick?: () => void;
}

const People: React.FC<PeopleProps> = ({ onPersonClick }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateMaxScroll = () => {
      if (carouselRef.current) {
        const maxScrollValue =
          carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
        setMaxScroll(maxScrollValue);
      }
    };

    updateMaxScroll();
    window.addEventListener("resize", updateMaxScroll);
    return () => window.removeEventListener("resize", updateMaxScroll);
  }, []);

  const scroll = (direction: "left" | "right") => {
    const track = carouselRef.current;
    if (!track) return;

    // Distância real entre o início de dois cartões (largura + espaçamento).
    // Medida no DOM porque o CSS muda esses valores por breakpoint.
    const slides = track.querySelectorAll<HTMLElement>(".people-carousel-slide");
    const step =
      slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 180;

    // Avança PEOPLE_PER_CLICK cartões, mas nunca mais do que cabe na área visível
    // (mínimo 1): em telas estreitas, andar 3 pularia pessoas que nem chegaram a aparecer.
    const visibleCards = Math.max(1, Math.floor(track.clientWidth / step));
    const cardsPerClick = Math.min(PEOPLE_PER_CLICK, visibleCards);

    // Alinha ao cartão mais próximo e anda `cardsPerClick` cartões
    const currentIndex = Math.round(track.scrollLeft / step);
    const targetIndex =
      direction === "left"
        ? currentIndex - cardsPerClick
        : currentIndex + cardsPerClick;
    // Limite medido na hora do clique (não depende de um valor guardado antes)
    const maxPosition = track.scrollWidth - track.clientWidth;
    const newPosition = Math.min(maxPosition, Math.max(0, targetIndex * step));

    track.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });
    setScrollPosition(newPosition);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      setScrollPosition(carouselRef.current.scrollLeft);
    }
  };

  return (
    <section className="people">
      <h2 className="people-title">Voadores</h2>
      <div className="people-carousel">
        <button
          className={`people-carousel-arrow people-carousel-arrow-left ${
            scrollPosition <= 0 ? "hidden" : ""
          }`}
          onClick={() => scroll("left")}
        >
          <span>‹</span>
        </button>

        <div className="people-carousel-container">
          <div
            ref={carouselRef}
            className={`people-carousel-track ${isDragging ? "dragging" : ""}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onScroll={handleScroll}
          >
            {people.map((person) => {
              const displayName = person.fullName ?? person.name;
              return (
                <div key={person.id} className="people-carousel-slide">
                  <Link
                    to={`/voadores/${person.id}`}
                    className="person-card"
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={onPersonClick}
                  >
                    <div className="person-image-container">
                      <img
                        src={person.image ?? "/pfps/anon.png"}
                        alt={displayName}
                        className="person-image"
                        draggable={false}
                      />
                    </div>
                    <div className="person-info">
                      <h3 className="person-name">{displayName}</h3>
                      {person.role && (
                        <p className="person-role">{person.role}</p>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
          <div className="people-fade-left"></div>
          <div className="people-fade-right"></div>
        </div>

        <button
          className={`people-carousel-arrow people-carousel-arrow-right ${
            scrollPosition >= maxScroll - 1 ? "hidden" : ""
          }`}
          onClick={() => scroll("right")}
        >
          <span>›</span>
        </button>
      </div>
    </section>
  );
};
export default People;
